import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Edit3, Palette, QrCode, Zap, BarChart3, Globe2, Shield, Headphones, CloudUpload, RotateCcw, XCircle, Play, Users, FileText, TrendingUp, Bell, Mail, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";
import { COMPANY } from "../config/company";
import ContactForm from "../components/ContactForm";
import useLocalizedSEO from "../hooks/useLocalizedSEO";
import { LANDING_JSON_LD } from "../lib/seoStructuredData";
import { CITIES } from "../lib/cities";

const FEATURES = [
  { icon: Edit3, title: { de: "Einfache Erstellung", en: "Easy Creation", tr: "Kolay Oluşturma" }, desc: { de: "Erstelle dein Menü in Minuten und veröffentliche es sofort.", en: "Create your menu in minutes and publish instantly.", tr: "Dakikalar içinde menünüzü oluşturun ve yayına alın." } },
  { icon: Bell, title: { de: "Live Tisch-Service ⭐", en: "Live Table Service ⭐", tr: "Canlı Masa Servisi ⭐" }, desc: { de: "Gäste rufen Kellner oder Rechnung direkt aus dem Menü — du siehst es sofort.", en: "Guests call waiter or bill straight from the menu — you see it instantly.", tr: "Müşteri garson veya hesap çağırır — anında dashboard'da görürsünüz." } },
  { icon: Palette, title: { de: "Anpassbar", en: "Customizable", tr: "Özelleştirilebilir" }, desc: { de: "5 Premium-Themes, Farben, Schriftarten — alles nach deinem Geschmack.", en: "5 premium themes, colors, fonts — fully tailored to you.", tr: "5 premium tema, renkler, fontlar — tamamen size özel." } },
  { icon: QrCode, title: { de: "QR-Code Generator", en: "QR Code Generator", tr: "QR Kod Üretimi" }, desc: { de: "Eindeutige QR-Codes für jeden Tisch oder jede Filiale.", en: "Unique QR codes for every table or location.", tr: "Her masa veya şube için özel QR kodlar." } },
  { icon: Zap, title: { de: "Sofort-Update", en: "Live Updates", tr: "Anlık Güncelleme" }, desc: { de: "Änderungen erscheinen sofort in deinem Live-Menü.", en: "Changes appear instantly in your live menu.", tr: "Menünüzdeki değişiklikler anında yayına alınır." } },
  { icon: Globe2, title: { de: "Mehrsprachig", en: "Multi-language", tr: "Çoklu Dil Desteği" }, desc: { de: "Präsentiere dein Menü in DE / EN / TR.", en: "Present your menu in DE / EN / TR.", tr: "Menünüzü DE/EN/TR olarak sunun." } },
];

const STEPS = [
  { icon: Users, title: { de: "Konto erstellen", en: "Create Account", tr: "Hesap Oluştur" }, desc: { de: "Kostenlos registrieren und dein Dashboard öffnen.", en: "Sign up free and open your dashboard.", tr: "Ücretsiz kaydolun ve panelinize giriş yapın." } },
  { icon: FileText, title: { de: "Menü erstellen", en: "Build Menu", tr: "Menünüzü Oluşturun" }, desc: { de: "Kategorien, Produkte, Preise und Bilder hinzufügen.", en: "Add categories, items, prices and images.", tr: "Kategoriler, ürünler, fiyatlar ve görselleri ekleyin." } },
  { icon: QrCode, title: { de: "QR-Code holen", en: "Get QR Code", tr: "QR Kodunuzu Alın" }, desc: { de: "Lade deinen QR-Code herunter und platziere ihn am Tisch.", en: "Download your QR and place it on tables.", tr: "Özel QR kodunuzu indirin ve masalarınıza yerleştirin." } },
  { icon: TrendingUp, title: { de: "Umsatz steigern", en: "Boost Sales", tr: "Satışlarınızı Artırın" }, desc: { de: "Verbessere die Erfahrung und steigere deine Bestellungen.", en: "Improve experience and increase orders.", tr: "Müşteri deneyimini iyileştirin, siparişlerinizi artırın." } },
];

export default function Landing() {
  const { t, lang } = useLang();
  const tx = (k) => k[lang] || k.de;

  // Dynamic SEO — title, description, keywords, hreflang & canonical per language
  useLocalizedSEO({
    titleByLang: {
      de: "Digitale QR Speisekarte für Restaurants | QR Menu Pro",
      en: "Digital QR Menu for Restaurants | QR Menu Pro",
      tr: "Restoranlar için Dijital QR Menü | QR Menu Pro",
    },
    descByLang: {
      de: "Erstelle in Minuten eine digitale QR-Speisekarte für dein Restaurant, Café oder Bistro. Schnell, modern, mobil — mehrsprachig (DE/EN/TR), GDPR-konform, Made in Europe.",
      en: "Create multilingual QR menus for your restaurant in minutes. Fast, modern and mobile-friendly. Multilingual (DE/EN/TR), GDPR-compliant, Made in Europe.",
      tr: "Restoranınız için dakikalar içinde çok dilli dijital QR menü oluşturun. Hızlı, modern ve mobil uyumlu — çok dilli (DE/EN/TR), KVKK/GDPR uyumlu, Avrupa malı.",
    },
    keywordsByLang: {
      de: "Digitale Speisekarte, QR Menü, Restaurant Menü System, QR-Code Speisekarte, kontaktlose Speisekarte, digitale Karte für Restaurant",
      en: "QR Menu for Restaurants, Digital Restaurant Menu, Restaurant Menu SaaS, QR code menu, contactless menu, restaurant menu builder",
      tr: "QR Menü Sistemi, Dijital Restoran Menüsü, QR Kod Menü, restoran menü sistemi, temassız menü, çok dilli menü",
    },
    jsonLdByLang: LANDING_JSON_LD,
    ogImage: "https://qrmenu-pro.com/og-image.png",
  });

  useEffect(() => {
    // no-op — kept for compatibility
  }, [lang]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-purple-50/40 to-white text-zinc-900 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-purple-950/30 dark:to-zinc-950 dark:text-zinc-100 relative overflow-hidden" data-testid="landing-page" style={{ marginTop: "-4rem", paddingTop: "4rem" }}>
      {/* Ambient glows */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-purple-400/20 blur-[120px] pointer-events-none" />
      <div className="absolute top-[400px] -left-40 w-[500px] h-[500px] rounded-full bg-fuchsia-300/15 blur-[120px] pointer-events-none" />

      {/* HERO */}
      <section className="relative">
        <div className="container mx-auto px-6 lg:px-12 pt-12 lg:pt-16 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-5 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {tx({ de: "Smart · Schnell · Anpassbar", en: "Smart · Fast · Customizable", tr: "Akıllı · Hızlı · Özelleştirilebilir" })}
              </div>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                {tx({
                  de: "Digitales QR Menü",
                  en: "Digital QR Menu",
                  tr: "Dijital QR Menü",
                })}
                <br />
                <span className="text-purple-400">
                  {tx({
                    de: "für Restaurants",
                    en: "for Restaurants",
                    tr: "Restoranlar için",
                  })}
                </span>
              </h1>
              <p className="mt-6 text-base lg:text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-md">
                {tx({
                  de: "Erstelle professionelle Speisekarten per QR Code für dein Restaurant. Einfach, modern und mobilfreundlich.",
                  en: "Create professional QR code menus for your restaurant. Simple, modern and mobile-friendly.",
                  tr: "Restoranınız için profesyonel QR kod menüler oluşturun. Basit, modern ve mobil uyumlu.",
                })}
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  tx({ de: "Unbegrenzte Menüs & Kategorien", en: "Unlimited menus & categories", tr: "Sınırsız Menü ve Kategori" }),
                  tx({ de: "Anpassbare Designs", en: "Customizable designs", tr: "Özelleştirilebilir Tasarımlar" }),
                  tx({ de: "Mehrsprachige Unterstützung", en: "Multi-language support", tr: "Çoklu Dil Desteği" }),
                  tx({ de: "Echtzeit-Bearbeitung", en: "Real-time editing", tr: "Gerçek Zamanlı Düzenleme" }),
                  tx({ de: "Analytik & Reporting", en: "Analytics & Reporting", tr: "Analitik ve Raporlama" }),
                ].map((line, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                    <div className="w-5 h-5 rounded-full bg-purple-500/20 grid place-items-center shrink-0">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />
                    </div>
                    {line}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" className="rounded-2xl bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30 group h-12 px-6" data-testid="hero-cta-primary">
                    {tx({ de: "15 Tage kostenlos testen", en: "Start 15-day free trial", tr: "15 gün ücretsiz dene" })}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                </Link>
                <Link to="/m/cafe-luna-demo">
                  <Button size="lg" variant="outline" className="rounded-2xl bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 text-zinc-900 dark:text-zinc-100 h-12 px-6" data-testid="hero-cta-demo">
                    <Play className="mr-2 h-4 w-4" />
                    {tx({ de: "Demo ansehen", en: "Watch demo", tr: "Demo İzle" })}
                  </Button>
                </Link>
              </div>
              <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
                {tx({ de: "Keine Kreditkarte erforderlich · 15 Tage volle Funktionen.", en: "No credit card required · Full features for 15 days.", tr: "Kredi kartı gerekmez · 15 gün tüm özellikler." })}
              </p>
            </div>

            {/* Center: Hero image - QR table stand */}
            <div className="lg:col-span-7 relative">
              {/* Soft glow halo behind image */}
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/20 via-fuchsia-400/10 to-transparent blur-3xl pointer-events-none" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-600/30 ring-1 ring-zinc-200 dark:ring-zinc-800 bg-white dark:bg-zinc-900">
                <img
                  src="/images/hero-table-qr.png"
                  alt={tx({
                    de: "QR-Code Tischaufsteller für digitale Speisekarte",
                    en: "QR code table stand for digital menu",
                    tr: "Dijital menü için QR kod masa standı",
                  })}
                  loading="eager"
                  fetchpriority="high"
                  width="1024"
                  height="1024"
                  className="w-full h-auto object-cover"
                  data-testid="hero-image"
                />
              </div>
              {/* Floating badges */}
              <div className="hidden lg:flex absolute -left-4 top-8 items-center gap-2 px-4 py-2 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-xl">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-bold">{tx({ de: "5 Premium Stile", en: "5 Premium Styles", tr: "5 Premium Stil" })}</span>
              </div>
              <div className="hidden lg:flex absolute -right-3 bottom-12 items-center gap-2 px-4 py-2 rounded-2xl bg-purple-600 text-white border border-purple-500 shadow-xl">
                <QrCode className="h-3.5 w-3.5" />
                <span className="text-xs font-bold">{tx({ de: "Bis zu 100 Tische", en: "Up to 100 tables", tr: "100 Masaya kadar" })}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO KEYWORDS STRIP — discoverable by Google, natural for visitors */}
      <section className="relative border-y border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 backdrop-blur-sm" aria-label="seo-keywords">
        <div className="container mx-auto px-6 lg:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { de: "QR Code Menü erstellen", en: "Create QR Code Menus", tr: "QR Kod Menü Oluştur" },
              { de: "Digitale Speisekarte", en: "Digital Menu Card", tr: "Dijital Menü Kartı" },
              { de: "Mehrsprachige Restaurant Menüs", en: "Multilingual Restaurant Menus", tr: "Çok Dilli Restoran Menüleri" },
              { de: "Kontaktlose Menükarte", en: "Contactless Menu", tr: "Temassız Menü" },
            ].map((kw, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-xl bg-purple-100 dark:bg-purple-950/50 grid place-items-center shrink-0">
                  <QrCode className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="font-semibold text-zinc-700 dark:text-zinc-200">{tx(kw)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE DEMOS SECTION — real public menus the customer can actually create */}
      <section id="demos" className="relative py-16 lg:py-24 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden" data-testid="demos-section">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-purple-500/5 blur-[140px]" />
        </div>
        <div className="relative container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" /> LIVE BEISPIELE
            </div>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              {tx({ de: "So sieht dein QR-Menü aus", en: "Here's what your QR menu looks like", tr: "QR menün böyle görünür" })}
            </h2>
            <p className="mt-4 text-base text-zinc-600 dark:text-zinc-400 leading-relaxed">
              {tx({
                de: "Drei echte Live-Beispiele, mit denen du genau das Layout, Kategorien, Bilder und Funktionen siehst, die du heute erstellen kannst — inklusive Mehrsprachigkeit, Kellner rufen und Rechnung anfragen.",
                en: "Three real live examples showing exactly the layout, categories, images and features you can build today — including multilingual, call waiter and request bill.",
                tr: "Bugün oluşturabileceğin tam düzeni, kategorileri, görselleri ve özellikleri gösteren üç gerçek canlı örnek — çoklu dil, garson çağırma ve hesap isteme dahil.",
              })}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                href: "/m/sultan-ocakbasi-demo",
                img: "https://images.unsplash.com/photo-1559847844-5315695dadae?w=900&q=80&auto=format&fit=crop",
                tag: { de: "TÜRKISCHES RESTAURANT", en: "TURKISH RESTAURANT", tr: "TÜRK RESTORANI" },
                title: "Sultan Ocakbaşı",
                desc: { de: "5 Kategorien · 11 Gerichte · Bilder · Mehrsprachig", en: "5 categories · 11 dishes · photos · multilingual", tr: "5 kategori · 11 ürün · foto · çok dilli" },
                testid: "demo-card-turkish",
              },
              {
                href: "/m/cafe-luna-demo",
                img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=900&q=80&auto=format&fit=crop",
                tag: { de: "CAFÉ", en: "CAFÉ", tr: "KAFE" },
                title: "Café Luna",
                desc: { de: "4 Kategorien · 9 Gerichte · Allergene · Tags", en: "4 categories · 9 dishes · allergens · tags", tr: "4 kategori · 9 ürün · alerjenler · etiketler" },
                testid: "demo-card-cafe",
              },
              {
                href: "/m/ember-blade-demo",
                img: "https://images.unsplash.com/photo-1544025162-d76694265947?w=900&q=80&auto=format&fit=crop",
                tag: { de: "STEAKHOUSE", en: "STEAKHOUSE", tr: "STEAKHOUSE" },
                title: "Ember & Blade",
                desc: { de: "5 Kategorien · 11 Gerichte · Premium-Preise", en: "5 categories · 11 dishes · premium pricing", tr: "5 kategori · 11 ürün · premium fiyatlar" },
                testid: "demo-card-steakhouse",
              },
            ].map((d) => (
              <Link
                key={d.href}
                to={d.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block overflow-hidden rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 hover:border-purple-500/50 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-purple-500/20"
                data-testid={d.testid}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={d.img} alt={d.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px] tracking-widest font-bold">
                      {tx(d.tag)}
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-xl font-bold tracking-tight text-white drop-shadow-lg">{d.title}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">{tx(d.desc)}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-purple-600 dark:text-purple-400">
                    {tx({ de: "Beispiel ansehen", en: "View example", tr: "Örneği gör" })}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Feature row — what every demo actually contains */}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
            {[
              { icon: Globe2, label: { de: "DE · EN · TR", en: "DE · EN · TR", tr: "DE · EN · TR" } },
              { icon: QrCode, label: { de: "Eindeutige QR-Codes", en: "Unique QR codes", tr: "Benzersiz QR kodlar" } },
              { icon: Bell, label: { de: "Kellner rufen (Pro)", en: "Call waiter (Pro)", tr: "Garson çağır (Pro)" } },
              { icon: FileText, label: { de: "Allergene · Tags · Bilder", en: "Allergens · tags · photos", tr: "Alerjenler · etiketler · foto" } },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-center gap-2.5 px-4 py-3 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
                  <Icon className="h-4 w-4 text-purple-600 dark:text-purple-400 shrink-0" />
                  <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">{tx(f.label)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="relative py-16 lg:py-20 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-widest text-purple-500 font-bold mb-2">{tx({ de: "Funktionen", en: "Features", tr: "Özellikler" })}</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              {tx({ de: "Alles, was dein Restaurant braucht", en: "Everything your restaurant needs", tr: "Restoranınızın ihtiyacı olan her şey" })}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-sm p-5 hover:border-purple-400 hover:shadow-md transition-all" data-testid={`feature-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-950/50 grid place-items-center mb-3">
                    <Icon className="h-5 w-5 text-purple-600" />
                  </div>
                  <h3 className="text-base font-bold mb-1">{tx(f.title)}</h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{tx(f.desc)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-16 lg:py-24 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-center tracking-tight mb-14">
            {tx({ de: "Wie funktioniert's?", en: "How does it work?", tr: "Nasıl Çalışır?" })}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-3 items-start relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <React.Fragment key={i}>
                  <div className="text-center px-4">
                    <div className="relative inline-block mb-4">
                      <div className="w-12 h-12 rounded-full bg-purple-600 grid place-items-center text-white font-bold text-lg">{i + 1}</div>
                      <div className="absolute -right-2 -bottom-2 w-7 h-7 rounded-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-sm grid place-items-center">
                        <Icon className="h-3.5 w-3.5 text-purple-600" />
                      </div>
                    </div>
                    <h3 className="font-bold mb-2">{tx(s.title)}</h3>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{tx(s.desc)}</p>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="hidden lg:flex absolute top-6 items-center justify-center" style={{ left: `${(i + 1) * 25 - 1.5}%`, width: "3%" }}>
                      <div className="w-full border-t border-dashed border-zinc-300 dark:border-zinc-700" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="relative py-16 lg:py-24 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 overflow-hidden" data-testid="contact-section" id="kontakt">
        {/* Decorative glows */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 left-1/4 w-[500px] h-[500px] rounded-full bg-fuchsia-500/10 blur-[120px]" />
          <div className="absolute -bottom-32 right-1/4 w-[500px] h-[500px] rounded-full bg-purple-500/10 blur-[120px]" />
        </div>

        <div className="relative container mx-auto px-6 lg:px-12 max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/30">
              <Mail className="h-3.5 w-3.5" />
              {tx({ de: "Wir sind für dich da", en: "We're here for you", tr: "Sizin için buradayız" })}
            </div>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              {tx({ de: "Fragen? Lass uns reden.", en: "Questions? Let's talk.", tr: "Soru mu var? Konuşalım." })}
            </h2>
            <p className="mt-4 text-base text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              {tx({
                de: "Schreib uns kurz — wir melden uns schnellstmöglich bei dir. Oder folge uns auf Instagram für News und Inspiration.",
                en: "Drop us a line — we'll get back to you as soon as possible. Or follow us on Instagram for news and inspiration.",
                tr: "Kısa bir mesaj yaz — en kısa sürede döneceğiz. Ya da haberler ve ilham için Instagram'da takip et.",
              })}
            </p>
          </div>

          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            {/* Form */}
            <ContactForm />

            {/* Side cards — Email + Instagram */}
            <aside className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-5">
              <a
                href={`mailto:${COMPANY.publicEmail}`}
                className="group block rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-purple-500/15 hover:border-purple-500/50"
                data-testid="contact-card-email"
              >
                <div className="w-11 h-11 rounded-2xl bg-purple-500/15 text-purple-600 dark:text-purple-400 grid place-items-center group-hover:bg-purple-600 group-hover:text-white transition-all duration-300 group-hover:scale-110">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-purple-600 dark:text-purple-400">{tx({ de: "E-Mail", en: "Email", tr: "E-posta" })}</div>
                  <div className="mt-1 font-bold text-base">{tx({ de: "Direkt schreiben", en: "Write us directly", tr: "Direkt yaz" })}</div>
                  <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 break-all">{COMPANY.publicEmail}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-purple-600 dark:text-purple-400">
                    {tx({ de: "E-Mail senden", en: "Send email", tr: "E-posta gönder" })}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </a>

              <a
                href={COMPANY.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-pink-500/15 hover:border-pink-500/50"
                data-testid="contact-card-instagram"
              >
                <div className="w-11 h-11 rounded-2xl bg-pink-500/15 text-pink-600 dark:text-pink-400 grid place-items-center group-hover:bg-gradient-to-tr group-hover:from-[#f09433] group-hover:via-[#dc2743] group-hover:to-[#bc1888] group-hover:text-white transition-all duration-300 group-hover:scale-110">
                  <Instagram className="h-5 w-5" />
                </div>
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-pink-600 dark:text-pink-400">Instagram</div>
                  <div className="mt-1 font-bold text-base">{tx({ de: "News & Inspiration", en: "News & inspiration", tr: "Haberler & ilham" })}</div>
                  <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">@{COMPANY.instagramHandle}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-pink-600 dark:text-pink-400">
                    {tx({ de: "Folgen", en: "Follow", tr: "Takip et" })}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="relative py-10 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 items-center">
            {[
              { icon: Shield, label: tx({ de: "100% Sicher", en: "100% Secure", tr: "%100 Güvenli" }) },
              { icon: Headphones, label: tx({ de: "24/7 Support", en: "24/7 Support", tr: "7/24 Destek" }) },
              { icon: CloudUpload, label: tx({ de: "Backup", en: "Backup", tr: "Yedekleme" }) },
              { icon: RotateCcw, label: tx({ de: "Unbegrenzte Updates", en: "Unlimited Updates", tr: "Sınırsız Güncelleme" }) },
              { icon: XCircle, label: tx({ de: "Jederzeit kündbar", en: "Cancel Anytime", tr: "İptal Edilebilir" }) },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="flex items-center gap-2.5 text-sm text-zinc-600 dark:text-zinc-300">
                  <div className="w-9 h-9 rounded-xl bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 grid place-items-center">
                    <Icon className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-xs">{t.label}</span>
                </div>
              );
            })}
            <div className="lg:ml-auto flex items-center gap-4 col-span-2 lg:col-span-1 justify-end">
              <div className="text-right">
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{tx({ de: "Ab", en: "From", tr: "Başlangıç Fiyatı" })}</div>
                <div className="text-2xl font-bold">20€<span className="text-xs text-zinc-500 dark:text-zinc-400">/{tx({ de: "Mon.", en: "mo.", tr: "ay" })}</span></div>
              </div>
              <Link to="/pricing">
                <Button className="rounded-2xl bg-purple-600 hover:bg-purple-700 text-white h-11 px-5" data-testid="cta-pricing-final">
                  {tx({ de: "Pakete ansehen", en: "View packages", tr: "Paketleri İncele" })}
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 dark:border-zinc-800 py-8 text-center text-xs text-zinc-500 dark:text-zinc-400 bg-white dark:bg-zinc-950 hidden">
        © {new Date().getFullYear()} qrmenu-pro.com · Made in Europe
      </footer>

      {/* CITY GRID — programmatic SEO landing pages */}
      <section className="relative" data-testid="landing-cities-grid">
        <div className="container mx-auto px-6 lg:px-12 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {tx({
              de: "Digitales QR-Menü in deiner Stadt",
              en: "Digital QR menu in your city",
              tr: "Şehrinizde dijital QR menü",
            })}
          </h2>
          <p className="mt-3 text-zinc-600 dark:text-zinc-400 max-w-2xl">
            {tx({
              de: "Restaurants in ganz Europa nutzen qrmenu-pro.com — hier sind unsere lokalen Stadtseiten:",
              en: "Restaurants across Europe use qrmenu-pro.com — explore our local city pages:",
              tr: "Avrupa genelinde restoranlar qrmenu-pro.com kullanıyor — yerel şehir sayfalarımız:",
            })}
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CITIES.map((c) => (
              <Link
                key={c.id}
                to={c.path}
                className="group rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-4 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all"
                data-testid={`landing-city-${c.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold">{c.localKeyword}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{c.name} · {c.country}</div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-zinc-400 group-hover:text-purple-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
